import type { TutorialAnchor } from "@v2/features/activation/constants/activationContent";
import type { AnalyticsEventParamsOf } from "@v2/features/infra/analytics/events";
import type { LucideIcon } from "lucide-react";
import type { User } from "schemas/generated/user";

import * as React from "react";

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@replo/design-system/components/sidebar/Sidebar";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  useCurrentProjectContext,
  useIsAgentEnabled,
} from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { TutorialAnchorWrapper } from "@v2/features/activation/components/HomepageTutorial";
import LearnMoreCollapsibleNavItem from "@v2/features/dashboard/sidebar/dashboard-sidebar-nav/LearnMoreCollapsibleNavItem";
import { DesignLibraryIcon } from "@v2/features/design-library/designLibraryIcon";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import {
  ChartLine,
  FlaskConical,
  GalleryVerticalEnd,
  Home,
  MessageSquareMore,
  PackageOpen,
  Plug,
  Settings,
} from "lucide-react";
import { generatePath, Link, useLocation } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";

type NavItem = {
  title: string;
  icon: LucideIcon;
  path: string;
  href: string;
  analyticsTab: AnalyticsEventParamsOf<"dashboard.nav.click">["tab"];
  tutorialAnchorId?: TutorialAnchor;
};

/**
 * Builds nav item groups based on the current project type.
 * Each group is separated by a divider in the sidebar.
 */
function buildNavItemGroups({
  project,
  workspaceId,
  isAgentEnabled,
}: {
  project: ReturnType<typeof useCurrentProjectContext>["project"];
  workspaceId: string | null;
  isAgentEnabled: boolean;
}): NavItem[][] {
  if (!project) {
    if (workspaceId) {
      return [
        [
          {
            title: "Settings",
            icon: Settings,
            path: `/workspace/${workspaceId}/settings`,
            href: `/workspace/${workspaceId}/settings`,
            analyticsTab: "settings",
          },
        ],
      ];
    }
    return [];
  }

  const isReplo = project.hostingType === "replo";
  const shouldShowNewIntegrationsUi = isFeatureEnabled("integrations-v2-ui");
  const baseRoute = isAgentEnabled ? "agent" : "shops";
  const experimentListPath =
    workspaceId &&
    generatePath(routes.shops.experiments.list, {
      projectId: project.id,
    });

  const groups: NavItem[][] = [];

  if (isReplo) {
    // Group 1: Home + Chat
    groups.push([
      {
        title: "Home",
        icon: Home,
        path: `/${baseRoute}/${project.id}/home`,
        href: `/${baseRoute}/${project.id}/home`,
        analyticsTab: "home",
      },
      {
        title: "Chat",
        icon: MessageSquareMore,
        path: `/${baseRoute}/${project.id}/chat`,
        href: `/${baseRoute}/${project.id}/chat`,
        analyticsTab: "chat",
      },
    ]);

    // Group 2: Analytics, A/B Testing
    const analyticsGroup: NavItem[] = [
      {
        title: "Analytics",
        icon: ChartLine,
        path: `/shops/${project.id}/analytics`,
        href: `/shops/${project.id}/analytics/home`,
        analyticsTab: "analytics_overview",
      },
    ];
    if (experimentListPath) {
      analyticsGroup.push({
        title: "A/B Testing",
        icon: FlaskConical,
        path: experimentListPath,
        href: experimentListPath,
        analyticsTab: "experiments",
      });
    }
    groups.push(analyticsGroup);

    // Group 3: Design Library, Products, Settings
    groups.push(
      filterNulls([
        !isAgentEnabled
          ? {
              title: "Design Library",
              icon: DesignLibraryIcon,
              path: `/shops/${project.id}/design-library`,
              href: `/shops/${project.id}/design-library`,
              analyticsTab: "design_library",
              tutorialAnchorId: "design-library" as TutorialAnchor,
            }
          : null,
        {
          title: "Products",
          icon: PackageOpen,
          path: `/shops/${project.id}/products`,
          href: `/shops/${project.id}/products`,
          analyticsTab: "products",
          tutorialAnchorId: "products" as TutorialAnchor,
        },
        shouldShowNewIntegrationsUi
          ? {
              title: "Integrations",
              icon: Plug,
              path: `/shops/${project.id}/integrations`,
              href: `/shops/${project.id}/integrations`,
              analyticsTab: "integrations",
            }
          : null,
      ]),
    );

    // group 4: settings
    groups.push([
      {
        title: "Settings",
        icon: Settings,
        path: `/shops/${project.id}/settings`,
        href: `/shops/${project.id}/settings`,
        analyticsTab: "settings",
      },
    ]);
  } else {
    // Shopify project: single group
    const items: NavItem[] = [
      {
        title: "Pages",
        icon: GalleryVerticalEnd,
        path: `/shops/${project.id}/site`,
        href: `/shops/${project.id}/site`,
        analyticsTab: "shop_site",
      },
      {
        title: "Analytics",
        icon: ChartLine,
        path: `/shops/${project.id}/analytics`,
        href: `/shops/${project.id}/analytics/home`,
        analyticsTab: "analytics_overview",
      },
    ];
    if (experimentListPath) {
      items.push({
        title: "A/B Testing",
        icon: FlaskConical,
        path: experimentListPath,
        href: experimentListPath,
        analyticsTab: "experiments",
      });
    }
    items.push({
      title: "Settings",
      icon: Settings,
      path: `/shops/${project.id}/settings`,
      href: `/shops/${project.id}/settings`,
      analyticsTab: "settings",
    });
    groups.push(items);
  }

  return groups;
}

const DashboardSidebarNavItem = ({
  item,
  workspaceId,
  user,
}: {
  item: NavItem;
  workspaceId: string | null;
  user: User | null;
}) => {
  const { open } = useSidebar();
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.path);
  const logEvent = useLogAnalytics();

  const menuButton = (
    <SidebarMenuItem
      key={item.title}
      // onClick={handleNavItemClick}
    >
      <SidebarMenuButton
        render={
          <Link
            to={item.href}
            onClick={() => {
              logEvent("dashboard.nav.click", {
                tab: item.analyticsTab,
                userId: user?.id ?? null,
                workspaceId: workspaceId ?? null,
              });
            }}
          />
        }
        tooltip={item.title}
        isActive={isActive}
        UNSAFE_className={twMerge(isActive && !open && "bg-default!")}
      >
        <item.icon className={twMerge(isActive && !open && "text-white")} />
        <span>{item.title}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  if (item.tutorialAnchorId) {
    return (
      <TutorialAnchorWrapper
        key={item.title}
        anchorId={item.tutorialAnchorId}
        side="right"
      >
        {menuButton}
      </TutorialAnchorWrapper>
    );
  }

  return menuButton;
};

export default function DashboardSidebarNav() {
  const { project } = useCurrentProjectContext();
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();
  const isAgentEnabled = useIsAgentEnabled();
  const navItems = buildNavItemGroups({
    project,
    workspaceId: workspaceId ?? null,
    isAgentEnabled,
  });

  return (
    <SidebarGroup UNSAFE_className="pt-0">
      <SidebarMenu>
        {navItems.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && <SidebarSeparator />}
            {group.map((item) => (
              <DashboardSidebarNavItem
                key={item.title}
                item={item}
                workspaceId={workspaceId ?? null}
                user={user ?? null}
              />
            ))}
          </React.Fragment>
        ))}
        <LearnMoreCollapsibleNavItem />
      </SidebarMenu>
    </SidebarGroup>
  );
}
